import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { RadioButtonGroup } from 'js/design_system/forms/RadioButtonGroup';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';

const Container = styled.div`
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 8px;
  background: var(--background-white, #ffffff);
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  appearance: none;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: none;
  border-bottom: 1px solid var(--stroke-light, #e2e8f0);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #e9ecef;
  }

  &:focus {
    outline: 0;
    box-shadow: inset 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary, #000);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const ConfigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--stroke-light, #e9ecef);

  &:last-child {
    border-bottom: none;
  }
`;

const ConfigLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #000);
`;

const ConfigInput = styled.input`
  padding: 6px 12px;
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 4px;
  font-size: 14px;
  max-width: 14ch;

  &:focus {
    outline: 0;
    border-color: var(--primary, #007bff);
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ConfigSelect = styled.select`
  flex: 1;
  padding: 6px 12px;
  border: 1px solid var(--stroke-light, #e2e8f0);
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: 0;
    border-color: var(--primary, #007bff);
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ConfigCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const ScopeSelector = styled.div`
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const Button = styled.button`
  appearance: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid var(--stroke-light, #e2e8f0);
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &.primary {
    color: #fff;
    background-color: var(--primary, #007bff);
    border-color: var(--primary, #007bff);

    &:hover {
      background-color: #0056b3;
      border-color: #0056b3;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    color: var(--text-primary, #000);
    background-color: #f8f9fa;

    &:hover {
      background-color: #e9ecef;
      border-color: #dee2e6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const Message = styled.div`
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;

  &.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  &.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
`;

type ConfigScope = 'global' | 'user';

interface ConfigurationProps {
  configuration: { [key: string]: unknown };
  reportId?: number;
}

// Special cases for select dropdowns with known options
const SELECT_OPTIONS: { [key: string]: string[] } = {
  search_strategy: ['auto_first', 'auto_top_n', 'auto_all', 'manual'],
  citation_search_method: [
    'academia_publication_search',
    'citation_search_agent',
    'refutation_finder',
    'web_search',
    'web_search_agentful',
    'mock',
  ],
};

const detectConfigType = (
  key: string,
  value: unknown,
): 'boolean' | 'number' | 'select' | 'string' => {
  // Check if this key has predefined select options
  if (SELECT_OPTIONS[key]) {
    return 'select';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  return 'string';
};

export const Configuration = ({
  configuration,
  reportId,
}: ConfigurationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scope, setScope] = useState<ConfigScope>('user');
  const [values, setValues] = useState<{ [key: string]: unknown }>(
    configuration || {},
  );
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const handleChange = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setValues(configuration || {});
    setMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const updates = Object.entries(values).filter(
        ([key, value]) => configuration[key] !== value,
      );

      if (updates.length === 0) {
        setMessage({ type: 'success', text: 'No changes to save' });
        setIsSaving(false);
        return;
      }

      // Convert updates array to object
      const updatesObject = Object.fromEntries(updates);

      // Send all updates in one request
      const response = await fetchWithCsrf(
        routes.api_v0_citeright_update_config_path({
          subdomain_param: 'api',
        }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            report_id: reportId,
            scope: scope,
            updates: updatesObject,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update configuration');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error instanceof Error
            ? error.message
            : 'Failed to save configuration',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderConfigInput = (key: string, value: unknown) => {
    const configType = detectConfigType(key, value);

    switch (configType) {
      case 'boolean':
        return (
          <ConfigCheckbox
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleChange(key, e.target.checked)}
          />
        );

      case 'number':
        return (
          <ConfigInput
            type="number"
            value={Number(value) || 0}
            onChange={(e) => handleChange(key, parseFloat(e.target.value))}
            step={key === 'overlap_percentage' ? '0.1' : '1'}
          />
        );

      case 'select':
        return (
          <ConfigSelect
            value={String(value)}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {SELECT_OPTIONS[key]?.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </ConfigSelect>
        );

      case 'string':
      default:
        return (
          <ConfigInput
            type="text"
            value={String(value)}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        );
    }
  };

  if (!currentUser || !currentUser.admin) {
    return (
      <Container>
        <Header onClick={() => setIsExpanded(!isExpanded)} type="button">
          <HeaderTitle>
            {isExpanded ? '▼' : '▶'} Configuration (Read-Only)
          </HeaderTitle>
        </Header>
        {isExpanded && (
          <Content>
            {Object.entries(configuration).map(([key, value]) => (
              <ConfigRow key={key}>
                <ConfigLabel>{key}</ConfigLabel>
                <Body size="sm">{String(value ?? 'N/A')}</Body>
              </ConfigRow>
            ))}
          </Content>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Header onClick={() => setIsExpanded(!isExpanded)} type="button">
        <HeaderTitle>{isExpanded ? '▼' : '▶'} Configuration</HeaderTitle>
      </Header>
      {isExpanded && (
        <Content>
          <ScopeSelector>
            <RadioButtonGroup
              label="Apply changes to:"
              options={['Current User Only', 'All Users (Global)']}
              value={
                scope === 'user' ? 'Current User Only' : 'All Users (Global)'
              }
              onChange={(selected) => {
                if (selected === 'Current User Only') {
                  setScope('user');
                } else if (selected === 'All Users (Global)') {
                  setScope('global');
                }
              }}
              small
            />
          </ScopeSelector>

          {Object.entries(values).map(([key, value]) => (
            <ConfigRow key={key}>
              <ConfigLabel>{key}</ConfigLabel>
              {renderConfigInput(key, value)}
            </ConfigRow>
          ))}

          {message && (
            <Message className={message.type}>{message.text}</Message>
          )}

          <ButtonGroup>
            <Button
              className="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button
              className="secondary"
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset
            </Button>
          </ButtonGroup>
        </Content>
      )}
    </Container>
  );
};
